import { inject, Pipe, PipeTransform } from '@angular/core';
import { BillingPeriodInterface, RevenuePeriod } from '@vendasta/sales-orders';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import dayjs from 'dayjs';

@Pipe({
  name: 'formatBillingPeriod',
})
export class FormatBillingPeriodPipe implements PipeTransform {
  private datePipe = inject(DatePipe);
  private translateService = inject(TranslateService);

  transform(billingPeriod?: BillingPeriodInterface, revenuePeriod?: RevenuePeriod): string {
    if (!billingPeriod) {
      return '';
    }

    const startDate = this.datePipe.transform(billingPeriod.startDate, 'MMM d, yyyy');
    if (!startDate) {
      return '';
    }

    if (
      !revenuePeriod ||
      !billingPeriod.endDate ||
      dayjs(billingPeriod.endDate).isSame(0) ||
      dayjs(billingPeriod.endDate).isBefore(startDate)
    ) {
      return startDate;
    }

    if (revenuePeriod === RevenuePeriod.YEARLY) {
      return this.formatYearlyBillingPeriod(startDate, billingPeriod.endDate);
    }

    return this.formatMonthlyBillingPeriod(startDate, billingPeriod.endDate);
  }

  private formatMonthlyBillingPeriod(startDate: string, endDate: Date): string {
    const numMonths = dayjs(endDate).diff(startDate, 'month');
    const translationProps: BillingPeriodTranslationProps = { startDate, numPeriods: numMonths };
    if (numMonths <= 0) {
      return startDate;
    }
    if (numMonths === 1) {
      return this.translateService.instant('BILLING_PERIOD.START_DATE_ONE_MONTH', translationProps);
    }

    return this.translateService.instant('BILLING_PERIOD.START_DATE_MANY_MONTHS', translationProps);
  }

  private formatYearlyBillingPeriod(startDate: string, endDate: Date): string {
    const numYears = dayjs(endDate).diff(startDate, 'year');
    const translationProps: BillingPeriodTranslationProps = { startDate, numPeriods: numYears };
    if (numYears <= 0) {
      return startDate;
    }
    if (numYears === 1) {
      return this.translateService.instant('BILLING_PERIOD.START_DATE_ONE_YEAR', translationProps);
    }

    return this.translateService.instant('BILLING_PERIOD.START_DATE_MANY_YEARS', translationProps);
  }
}

export type BillingPeriodTranslationProps = {
  startDate: string;
  numPeriods: number;
};
